import {
  useCallback,
  useEffect,
  useLayoutEffect,
  useRef,
  useState,
} from "react";
import mixpanel from "mixpanel-browser";
import { useMutation } from "@apollo/client";
import { useSearchParams } from "react-router-dom";

import { CREATE_CONSUMER_CHECKOUT_URL } from "../../../api/gqlQueries";
import UserRegistrationError from "../../../components/errors/UserRegistrationError";
import { setToken } from "../../../utils/auth_token";
import slideAutomation from "../../../img/paywall/slide-automation.png";
import slideEnergy from "../../../img/paywall/slide-energy.png";
import slideSecurity from "../../../img/paywall/slide-security.png";
import slideShelly from "../../../img/paywall/slide-shelly.png";
import slideSmartSetup from "../../../img/paywall/slide-smart-setup.png";
import slideSupport from "../../../img/paywall/slide-support.png";
import slideUpgrade from "../../../img/paywall/slide-upgrade.png";

type PlanType = "monthly" | "annual";

// Mobile identifies its Mixpanel distinct_id with the Brilliant userId. On iOS
// that is the raw userId, which for multifamily/operator accounts carries an
// "MF" prefix; the server and Android use the externalUserId, which strips it.
// Normalize here so the paywall's distinct_id and user_id match across
// platforms and server-side events.
const toExternalUserId = (userId: string): string =>
  userId.startsWith("MF") ? userId.slice(2) : userId;

interface Slide {
  description: string;
  illustrationAlt: string;
  illustrationSrc: string;
  title: string;
}

// ─── Slide data ───────────────────────────────────────────────────────────────

const SLIDES: Slide[] = [
  {
    description:
      "Get access to an ever growing set of Brilliant Modes, with advanced automations that bring your smart home to life.",
    illustrationAlt: "Advanced Automations Modes",
    illustrationSrc: slideAutomation,
    title: "Advanced Automations Modes",
  },
  {
    description:
      "Not sure where to start? Brilliant Max gives you personalized automation recommendations, making it easy to get the most out of your smart home devices.",
    illustrationAlt: "Smart Setup Recommendation",
    illustrationSrc: slideSmartSetup,
    title: "Smart Setup Recommendation",
  },
  {
    description:
      "Away and Vacation Modes make sure things are turned off and secure when you are away from home. Plus presence detection and evening light randomization for extra peace of mind.",
    illustrationAlt: "Peace of Mind",
    illustrationSrc: slideSecurity,
    title: "Peace of Mind",
  },
  {
    description:
      "Setup geofences in the Brilliant Mobile app, and have your smart home run in the background.",
    illustrationAlt: "Easy Automation",
    illustrationSrc: slideEnergy,
    title: "Easy Automation",
  },
  {
    description:
      "Get access to concierge-level support from our in-house team, who can configure devices for you whenever you need assistance.",
    illustrationAlt: "Premium Onboarding & Support",
    illustrationSrc: slideSupport,
    title: "Premium Onboarding & Support",
  },
  {
    description:
      "Receive a complimentary Door/Window or Motion sensor when you sign up and enable Smart HVAC and other advanced automations.",
    illustrationAlt: "Complimentary Shelly Products",
    illustrationSrc: slideShelly,
    title: "Complimentary Shelly Products",
  },
  {
    description:
      "Existing Brilliant users who become annual subscribers can upgrade to Second Gen Controls at a discount and take advantage of improved connectivity and faster processors.",
    illustrationAlt: "Brilliant Upgrade Program",
    illustrationSrc: slideUpgrade,
    title: "Brilliant Upgrade Program",
  },
];

// ─── Component ────────────────────────────────────────────────────────────────

const ConsumerPaywall: React.FC = () => {
  const [activeSlide, setActiveSlide] = useState(0);
  const [containerWidth, setContainerWidth] = useState(0);
  const [selectedPlan, setSelectedPlan] = useState<PlanType>("annual");

  const [searchParams] = useSearchParams();
  const homeId = searchParams.get("home_id") ?? "";
  const token = searchParams.get("token") ?? "";
  const externalUserId = toExternalUserId(searchParams.get("user_id") ?? "");

  const [createCheckoutUrl, { error, loading }] = useMutation(
    CREATE_CONSUMER_CHECKOUT_URL
  );

  const activeSlideRef = useRef(0);
  const carouselRef = useRef<HTMLDivElement>(null);
  const intervalRef = useRef<ReturnType<typeof setInterval> | null>(null);
  const touchStartXRef = useRef(0);

  useEffect(() => {
    // Align this webview's distinct_id with mobile/server so subscription
    // events attribute to the same Brilliant user.
    if (externalUserId) {
      mixpanel.identify(externalUserId);
    }
  }, [externalUserId]);

  // Declared after the identify effect so React runs it second on mount and the
  // impression attributes to the identified profile rather than an anonymous device.
  // Deliberately not max_paywall_viewed: that event is the native in-app upsell
  // (source mode_locked / control-qrcode), a different screen from this plan chooser.
  useEffect(() => {
    mixpanel.track("max_web_paywall_viewed", {
      home_id: homeId,
      user_id: externalUserId,
    });
  }, []);

  useLayoutEffect(() => {
    if (carouselRef.current) {
      setContainerWidth(carouselRef.current.offsetWidth);
    }
  }, []);

  useEffect(() => {
    const el = carouselRef.current;
    if (!el) return undefined;
    const ro = new ResizeObserver(() => {
      setContainerWidth(el.offsetWidth);
    });
    ro.observe(el);
    return () => ro.disconnect();
  }, []);

  const goToSlide = useCallback((index: number) => {
    activeSlideRef.current = index;
    setActiveSlide(index);
    if (intervalRef.current) clearInterval(intervalRef.current);
    intervalRef.current = setInterval(() => {
      const next = (activeSlideRef.current + 1) % SLIDES.length;
      activeSlideRef.current = next;
      setActiveSlide(next);
    }, 5000);
  }, []);

  useEffect(() => {
    intervalRef.current = setInterval(() => {
      const next = (activeSlideRef.current + 1) % SLIDES.length;
      activeSlideRef.current = next;
      setActiveSlide(next);
    }, 5000);
    return () => {
      if (intervalRef.current) clearInterval(intervalRef.current);
    };
  }, []);

  const handleTouchEnd = useCallback(
    (e: React.TouchEvent) => {
      const delta = e.changedTouches[0].clientX - touchStartXRef.current;
      if (Math.abs(delta) > 50) {
        goToSlide(
          delta < 0
            ? (activeSlideRef.current + 1) % SLIDES.length
            : (activeSlideRef.current - 1 + SLIDES.length) % SLIDES.length
        );
      }
    },
    [goToSlide]
  );

  const handleTouchStart = useCallback((e: React.TouchEvent) => {
    touchStartXRef.current = e.touches[0].clientX;
  }, []);

  const handleSelectPlan = useCallback(async () => {
    mixpanel.track("max_plan_selected", {
      home_id: homeId,
      plan_type: selectedPlan,
      user_id: externalUserId,
    });
    if (token) setToken(token);
    try {
      const { data } = await createCheckoutUrl({
        variables: { homeId, plan: selectedPlan },
      });
      const url = data?.createHomeSubscriptionCheckout?.checkoutUrl;
      if (url) window.location.href = url;
    } catch {
      // useMutation's error state handles the UI
    }
  }, [createCheckoutUrl, externalUserId, homeId, selectedPlan, token]);

  const trackStyle = {
    transform: containerWidth
      ? `translateX(-${activeSlide * containerWidth}px)`
      : "none",
    width: containerWidth
      ? `${SLIDES.length * containerWidth}px`
      : `${SLIDES.length * 100}%`,
  };

  const slideStyle = {
    width: containerWidth ? `${containerWidth}px` : "100%",
  };

  if (error) {
    return (
      <UserRegistrationError
        description="There was a problem processing your subscription. Please try again or contact support."
        name="Something went wrong"
      />
    );
  }

  return (
    <div className="consumer-paywall">
      <header className="consumer-paywall-header">
        <h1 className="consumer-paywall-title">Brilliant Max</h1>
      </header>

      <div
        className="consumer-paywall-carousel"
        onTouchEnd={handleTouchEnd}
        onTouchStart={handleTouchStart}
        ref={carouselRef}
      >
        <div className="consumer-paywall-track" style={trackStyle}>
          {SLIDES.map((slide, i) => (
            <div
              aria-hidden={i !== activeSlide}
              className="consumer-paywall-slide"
              key={slide.title}
              style={slideStyle}
            >
              <div className="consumer-paywall-illustration">
                <img alt={slide.illustrationAlt} src={slide.illustrationSrc} />
              </div>
              <div className="consumer-paywall-slide-text">
                <h2 className="consumer-paywall-slide-title">{slide.title}</h2>
                <p className="consumer-paywall-slide-description">
                  {slide.description}
                </p>
              </div>
            </div>
          ))}
        </div>
      </div>

      <div
        aria-label="Feature highlights"
        className="consumer-paywall-pagination"
        role="tablist"
        tabIndex={0}
      >
        {SLIDES.map((slide, i) => (
          <button
            aria-label={`Slide ${i + 1}: ${slide.title}`}
            aria-selected={i === activeSlide}
            className={`consumer-paywall-dot${i === activeSlide ? " consumer-paywall-dot--active" : ""}`}
            key={slide.title}
            onClick={() => goToSlide(i)}
            role="tab"
            type="button"
          />
        ))}
      </div>

      <div className="consumer-paywall-pricing">
        <button
          className={`consumer-paywall-plan-card${selectedPlan === "annual" ? " consumer-paywall-plan-card--selected" : ""}`}
          onClick={() => setSelectedPlan("annual")}
          type="button"
        >
          <span className="consumer-paywall-plan-tag">SAVE 16%</span>
          <span className="consumer-paywall-plan-name">Pay Yearly</span>
          <span className="consumer-paywall-plan-price">$99.99</span>
          <span className="consumer-paywall-plan-period">($8.33/mo)</span>
        </button>

        <button
          className={`consumer-paywall-plan-card${selectedPlan === "monthly" ? " consumer-paywall-plan-card--selected" : ""}`}
          onClick={() => setSelectedPlan("monthly")}
          type="button"
        >
          <span className="consumer-paywall-plan-name">Pay Monthly</span>
          <span className="consumer-paywall-plan-price">$9.99</span>
        </button>
      </div>

      <button
        className="consumer-paywall-cta"
        disabled={loading}
        onClick={handleSelectPlan}
        type="button"
      >
        {loading ? "Loading..." : "Select Plan"}
      </button>

      <p className="consumer-paywall-disclaimer">
        30-day free trial is available for new members only. Cancel anytime.
      </p>
    </div>
  );
};

export default ConsumerPaywall;
